import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Dropdown,
  Empty,
  Descriptions,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Row,
  Space,
  Table,
  Tag,
  Select,
  Spin,
  Switch,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  SearchOutlined,
  MoreOutlined,
  LinkOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  ReloadOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
  PlusOutlined,
  MinusOutlined,
  WalletOutlined,
  RollbackOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useSelection } from "../SelectionContext";
import { useEcommercePermissions } from "../hooks/useEcommercePermissions";
import { PermissionDenied } from "./components";
import dayjs from "dayjs";
import API_CONFIG from "../config/api.js";
import ShippingAddressDisplay from "./components/ShippingAddressDisplay";
import FulfillmentQueue, { Fulfillment, FulfillmentSummary } from "./components/FulfillmentQueue";
import "./EcommerceAdmin.css";
import "./OrderEditModal.css";
import { EcommerceFilterField, useEcommerceWorkspaceStyle, useEcommerceTableHeight } from "./EcommerceWorkspace";
import { ecommerceRequestHeaders, formatOrderTimestamp, shortenOrderReference } from "./ecommerceWorkspaceUtils";
import type { MenuProps } from "antd";
import DeliveryFinancePanel from "./components/DeliveryFinancePanel";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_URL = API_CONFIG.BASE_URL;

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string | null;
  total_amount: number;
  status: string;
  payment_status: string;
  payment_method?: string;
  payment_reconciliation_status?: string;
  payment_reconciliation_message?: string | null;
  promotion_code?: string | null;
  discount_amount?: number;
  created_at: string;
  store_name: string;
  store_slug: string;
  items_count: number;
}

interface OrderStats {
  total_orders: number;
  pending_orders: number;
  completed_orders: number;
  total_revenue: number;
  merchandise_revenue?: number;
  shipping_revenue?: number;
  total_invoiced?: number;
}

interface ShippingAddress {
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
}

interface OrderItem {
  id: number;
  product_name: string;
  product_description?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface OrderDetails {
  fulfillments?: Fulfillment[];
  fulfillment_mode?: string;
  store_id?: number;
  id: number;
  order_number: string;
  customer_name?: string;
  customer_email: string | null;
  customer_phone?: string;
  store_name?: string;
  total_amount: number;
  shipping_amount?: number;
  tax_amount?: number;
  status: string;
  payment_status: string;
  payment_method?: string;
  payment_reference?: string;
  promotion_code?: string | null;
  discount_type?: 'percent' | 'fixed' | null;
  discount_value?: number | null;
  discount_amount?: number;
  payment_reconciliation_status?: string;
  payment_reconciliation_message?: string | null;
  payment_reconciled_at?: string | null;
  shipping_address?: ShippingAddress | null;
  billing_address?: ShippingAddress | null;
  notes?: string;
  created_at: string;
  updated_at?: string;
  items?: OrderItem[];
  payment_summary?: PaymentSummary;
  payments?: EcommercePaymentEntry[];
}

interface PaymentSummary {
  captured_amount: number;
  refunded_amount: number;
  net_collected_amount: number;
  capture_count: number;
  refund_count: number;
}

interface EcommercePaymentEntry {
  id: number;
  event_type: "capture" | "refund";
  amount: number;
  payment_id: number;
  payment_number: string;
  payment_date: string;
  payment_mode_name?: string;
  treasury_account_name?: string;
  provider?: string;
  provider_reference?: string;
}

interface PaymentOption {
  id: number;
  name: string;
  mode_type?: string;
  account_type?: string;
  currency_code?: string;
  is_default?: number | boolean;
}

interface OrderPaymentOptions {
  options: {
    payment_modes: PaymentOption[];
    treasury_accounts: PaymentOption[];
  };
  suggested_route?: {
    payment_mode_id: number;
    treasury_account_id: number;
  } | null;
  summary: PaymentSummary;
}

const ORDER_STATUS_OPTIONS = [
  { value: "pending", label: "En attente", color: "orange" },
  { value: "confirmed", label: "Confirmée", color: "blue" },
  { value: "processing", label: "En préparation", color: "cyan" },
  { value: "shipped", label: "Expédiée", color: "purple" },
  { value: "delivered", label: "Livrée", color: "green" },
  { value: "cancelled", label: "Annulée", color: "red" },
] as const;

const ORDER_STATUS_TRANSITIONS: Record<string, readonly string[]> = {
  pending: ["confirmed", "processing", "shipped", "delivered", "cancelled"],
  confirmed: ["processing", "shipped", "delivered", "cancelled"],
  processing: ["shipped", "delivered", "cancelled"],
  shipped: ["delivered", "cancelled"],
  delivered: [],
  cancelled: ["pending"],
  refunded: [],
};

const getAllowedStatusOptions = (currentStatus: string) => {
  const allowed = new Set([
    currentStatus,
    ...(ORDER_STATUS_TRANSITIONS[currentStatus] || []),
  ]);
  return ORDER_STATUS_OPTIONS.filter((option) => allowed.has(option.value));
};

const toSafeNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const numericValue = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isNaN(numericValue) ? 0 : numericValue;
};

const formatFcfa = (value: number) =>
  `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)} FCFA`;

const isFinalizedOrderStatus = (status?: string) =>
  status === "delivered" || status === "refunded";

const CommandesEnLigne: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<OrderStats>({
    total_orders: 0,
    pending_orders: 0,
    completed_orders: 0,
    total_revenue: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    payment_status: "",
    payment_reconciliation_status: "",
    date_range: null as any,
    store_id: "",
  });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState<Order | null>(null);
  const [editOrderDetails, setEditOrderDetails] = useState<OrderDetails | null>(
    null
  );
  const [editLoading, setEditLoading] = useState(false);
  const [editForm] = Form.useForm();
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentOptionsLoading, setPaymentOptionsLoading] = useState(false);
  const [paymentActionLoading, setPaymentActionLoading] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState<OrderDetails | null>(null);
  const [paymentOptions, setPaymentOptions] =
    useState<OrderPaymentOptions | null>(null);
  const [paymentForm] = Form.useForm();
  const [manualDiscountEnabled, setManualDiscountEnabled] = useState(false);
  const manualDiscountType = Form.useWatch('manual_discount_type', paymentForm);
  const manualDiscountValue = Form.useWatch('manual_discount_value', paymentForm);
  const editedItems = Form.useWatch("items", editForm) || [];
  const editedShippingAmount = Form.useWatch("shipping_amount", editForm);
  const editedTaxAmount = Form.useWatch("tax_amount", editForm);
  const paymentSubtotal = (paymentOrder?.items || []).reduce(
    (sum, item) => sum + Number(item.quantity) * Number(item.unit_price), 0,
  );
  const previewDiscount = manualDiscountEnabled && Number(manualDiscountValue) > 0
    ? Math.min(Math.round(paymentSubtotal * 100), manualDiscountType === 'percent'
      ? Math.round(Math.round(paymentSubtotal * 100) * Number(manualDiscountValue) / 100)
      : Math.round(Number(manualDiscountValue) * 100)) / 100
    : 0;
  const paymentPreviewTotal = Math.round((Number(paymentOrder?.total_amount || 0) - previewDiscount) * 100) / 100;
  const editedSubtotal = editedItems.reduce(
    (sum: number, item: any) =>
      sum + toSafeNumber(item?.quantity) * toSafeNumber(item?.unit_price),
    0
  );
  const editedDiscountValue = toSafeNumber(editOrderDetails?.discount_value);
  const editedDiscountAmount =
    editOrderDetails?.discount_type === "percent"
      ? Math.min(
          Math.round(editedSubtotal * 100),
          Math.round(
            (Math.round(editedSubtotal * 100) * editedDiscountValue) / 100
          )
        ) / 100
      : editOrderDetails?.discount_type === "fixed"
      ? Math.min(
          Math.round(editedSubtotal * 100),
          Math.round(editedDiscountValue * 100)
        ) / 100
      : 0;
  const editedFinalTotal =
    Math.round(
      (editedSubtotal +
        toSafeNumber(editedShippingAmount) +
        toSafeNumber(editedTaxAmount) -
        editedDiscountAmount) *
        100
    ) / 100;
  const editedInvalidItemsCount = editedItems.filter(
    (item: any) => !item?.product_id
  ).length;
  const editedOrderInitialStatus =
    editOrderDetails?.status || orderToEdit?.status || "pending";
  const editedOrderIsFinalized = isFinalizedOrderStatus(
    editedOrderInitialStatus
  );

  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const { selectedWarehouse, selectedCompany } = useSelection();
  const workspaceStyle = useEcommerceWorkspaceStyle();
  const { tableRef, tableHeight } = useEcommerceTableHeight();
  const scopeKey = `${selectedCompany || ""}:${selectedWarehouse || ""}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const statsScopeKey = `${scopeKey}:${filters.store_id}`;
  const statsScopeRef = useRef(statsScopeKey);
  statsScopeRef.current = statsScopeKey;
  const mountedRef = useRef(true);
  const ordersRequestRef = useRef(0);
  const statsRequestRef = useRef(0);
  const storesRequestRef = useRef(0);
  const detailsRequestRef = useRef(0);
  const editRequestRef = useRef(0);
  const paymentRequestRef = useRef(0);
  const readControllers = useRef<{ orders?: AbortController; stats?: AbortController; stores?: AbortController }>({});
  const [ordersError, setOrdersError] = useState("");
  const [statsError, setStatsError] = useState("");
  const [storesError, setStoresError] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(false);
  const [paymentShortcutLoading, setPaymentShortcutLoading] = useState<number | null>(null);
  const [statusActionId, setStatusActionId] = useState<number | null>(null);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [queueSummary, setQueueSummary] = useState({ loading: true, total: 0, error: "", status: "" });
  const updateQueueSummary = useCallback((summary: { loading: boolean; total: number; error: string; status: string }) => setQueueSummary(summary), []);
  const {
    canViewOrders,
    canProcessOrders,
    canEditOrders,
    canDeleteOrders,
    hasAnyEcommercePermission,
    getPermissionErrorMessage,
  } = useEcommercePermissions();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(value);

  const hasGeneralAccess = hasAnyEcommercePermission();
  const hasOrderAccess = canViewOrders();

  useEffect(() => {
    mountedRef.current = true;
    const controllers = readControllers.current;
    return () => {
      mountedRef.current = false;
      Object.values(controllers).forEach((controller) => controller?.abort());
    };
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!selectedWarehouse || !hasOrderAccess || scopeKey !== scopeRef.current) return;
    const requestId = ++ordersRequestRef.current;
    const requestScope = scopeKey;
    readControllers.current.orders?.abort();
    const controller = new AbortController();
    readControllers.current.orders = controller;
    const stale = () => !mountedRef.current || controller.signal.aborted ||
      requestId !== ordersRequestRef.current || requestScope !== scopeRef.current;
    setLoading(true);
    setOrdersError("");
    try {
      const params = new URLSearchParams({
        page: String(currentPage), limit: String(currentPageSize), warehouse_id: String(selectedWarehouse),
      });
      if (filters.search) params.set("search", filters.search);
      if (filters.status) params.set("status", filters.status);
      if (filters.payment_status) params.set("payment_status", filters.payment_status);
      if (filters.payment_reconciliation_status) params.set("payment_reconciliation_status", filters.payment_reconciliation_status);
      if (filters.store_id) params.set("store_id", filters.store_id);
      if (filters.date_range?.[0] && filters.date_range?.[1]) {
        params.set("start_date", filters.date_range[0].format("YYYY-MM-DD"));
        params.set("end_date", filters.date_range[1].format("YYYY-MM-DD"));
      }
      const response = await fetch(`${API_URL}/api/ecommerce/orders?${params}`, {
        headers: ecommerceRequestHeaders(), signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (stale()) return;
      if (!response.ok) throw new Error(data.error || "Impossible de charger les commandes.");
      if (!Array.isArray(data.orders)) throw new Error("Réponse de la liste des commandes invalide.");
      setOrders(data.orders);
      const total = Number(data.total) || 0;
      setPagination((previous) => ({ ...previous, total,
        current: Math.min(previous.current, Math.max(1, Math.ceil(total / previous.pageSize))) }));
    } catch (error: any) {
      if (!stale()) {
        setOrders([]);
        setPagination((previous) => ({ ...previous, total: 0 }));
        setOrdersError(error.message || "Connexion aux commandes impossible.");
      }
    } finally {
      if (!stale()) setLoading(false);
    }
  }, [selectedWarehouse, scopeKey, hasOrderAccess, filters, currentPage, currentPageSize]);

  const fetchStats = useCallback(async () => {
    if (!selectedWarehouse || !hasOrderAccess || statsScopeKey !== statsScopeRef.current) return;
    const requestId = ++statsRequestRef.current;
    const requestScope = statsScopeKey;
    readControllers.current.stats?.abort();
    const controller = new AbortController();
    readControllers.current.stats = controller;
    const stale = () => !mountedRef.current || controller.signal.aborted ||
      requestId !== statsRequestRef.current || requestScope !== statsScopeRef.current;
    setStatsLoading(true);
    setStatsError("");
    try {
      const statsParams = new URLSearchParams({ warehouse_id: String(selectedWarehouse) });
      if (filters.store_id) statsParams.set("store_id", String(filters.store_id));
      const response = await fetch(`${API_URL}/api/ecommerce/orders/stats?${statsParams}`, {
        headers: ecommerceRequestHeaders(), signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (stale()) return;
      if (!response.ok) throw new Error(data.error || "Indicateurs indisponibles.");
      setStats(data);
    } catch (error: any) {
      if (!stale()) setStatsError(error.message || "Indicateurs indisponibles.");
    } finally {
      if (!stale()) setStatsLoading(false);
    }
  }, [selectedWarehouse, statsScopeKey, hasOrderAccess, filters.store_id]);

  const fetchStores = useCallback(async () => {
    if (!selectedWarehouse || !hasOrderAccess || scopeKey !== scopeRef.current) return;
    const requestId = ++storesRequestRef.current;
    const requestScope = scopeKey;
    readControllers.current.stores?.abort();
    const controller = new AbortController();
    readControllers.current.stores = controller;
    const stale = () => !mountedRef.current || controller.signal.aborted ||
      requestId !== storesRequestRef.current || requestScope !== scopeRef.current;
    setStoresLoading(true);
    setStoresError("");
    try {
      const response = await fetch(`${API_URL}/api/ecommerce/stores?warehouse_id=${selectedWarehouse}`, {
        headers: ecommerceRequestHeaders(), signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (stale()) return;
      if (!response.ok || !Array.isArray(data.stores)) throw new Error(data.error || "Boutiques indisponibles.");
      setStores(data.stores);
    } catch (error: any) {
      if (!stale()) {
        setStores([]);
        setStoresError(error.message || "Boutiques indisponibles.");
      }
    } finally {
      if (!stale()) setStoresLoading(false);
    }
  }, [selectedWarehouse, scopeKey, hasOrderAccess]);

  useEffect(() => {
    Object.values(readControllers.current).forEach((controller) => controller?.abort());
    setOrders([]);
    setStores([]);
    setFilters({ search: "", status: "", payment_status: "", payment_reconciliation_status: "", date_range: null, store_id: "" });
    setPagination((previous) => ({ ...previous, current: 1, total: 0 }));
    setDetailModalVisible(false);
    setEditModalVisible(false);
    setDeleteModalVisible(false);
    setPaymentModalVisible(false);
    setOrderDetails(null);
    setEditOrderDetails(null);
    setPaymentOrder(null);
    setSelectedOrder(null);
    setPaymentShortcutLoading(null);
    setOrdersError("");
    setStatsError("");
    setStoresError("");
    setQueueSummary({ loading: true, total: 0, error: "", status: "" });
  }, [scopeKey]);

  useEffect(() => {
    if (selectedWarehouse && hasOrderAccess) void fetchOrders();
    else {
      setOrders([]);
      setLoading(false);
    }
    const controllers = readControllers.current;
    return () => controllers.orders?.abort();
  }, [fetchOrders, selectedWarehouse, hasOrderAccess]);

  useEffect(() => {
    if (selectedWarehouse && hasOrderAccess) {
      void fetchStats();
      void fetchStores();
    } else {
      setStats({ total_orders: 0, pending_orders: 0, completed_orders: 0, total_revenue: 0 });
      setStores([]);
    }
    const controllers = readControllers.current;
    return () => {
      controllers.stats?.abort();
      controllers.stores?.abort();
    };
  }, [fetchStats, fetchStores, selectedWarehouse, hasOrderAccess]);

  const loadOrderDetails = async (id: number): Promise<OrderDetails> => {
    const response = await fetch(`${API_URL}/api/ecommerce/orders/${id}`, { headers: ecommerceRequestHeaders() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.order) throw new Error(data.error || "Impossible de charger le détail de la commande.");
    const parseAddress = (value: any) => {
      if (typeof value !== "string") return value || null;
      try { return JSON.parse(value); } catch { return null; }
    };
    return {
      ...data.order, items: data.items || [], fulfillments: data.fulfillments || [],
      shipping_address: parseAddress(data.order.shipping_address),
      billing_address: parseAddress(data.order.billing_address),
      payment_summary: data.payment_summary, payments: data.payments || [],
    };
  };

  const handleViewOrder = async (order: Order) => {
    if (!canViewOrders()) return;
    const requestScope = scopeRef.current;
    const requestId = ++detailsRequestRef.current;
    const current = () => mountedRef.current && requestScope === scopeRef.current && requestId === detailsRequestRef.current;
    setSelectedOrder(order);
    setDetailModalVisible(true);
    setOrderDetailsLoading(true);
    setOrderDetails(null);
    try {
      const details = await loadOrderDetails(order.id);
      if (current()) setOrderDetails(details);
    } catch (error: any) {
      if (current()) {
        message.error(error.message);
        setDetailModalVisible(false);
      }
    } finally {
      if (current()) setOrderDetailsLoading(false);
    }
  };

  const openPaymentFromList = async (order: Order) => {
    if (!canProcessOrders() || paymentShortcutLoading !== null) return;
    const requestScope = scopeRef.current;
    const requestId = ++paymentRequestRef.current;
    const current = () => mountedRef.current && requestScope === scopeRef.current && requestId === paymentRequestRef.current;
    setPaymentShortcutLoading(order.id);
    try {
      const details = await loadOrderDetails(order.id);
      if (!current()) return;
      const canCollect = details.payment_status === "pending" && !["cancelled", "refunded"].includes(details.status);
      const canReconcile = details.payment_status === "paid" && details.payment_reconciliation_status !== "recorded";
      if (!canCollect && !canReconcile) {
        message.info("Le paiement a changé. Consultez le détail actualisé de cette commande.");
        await fetchOrders();
        return;
      }
      await openPaymentModal(details);
    } catch (error: any) {
      if (current()) message.error(error.message || "Chargement du paiement impossible.");
    } finally {
      if (mountedRef.current && requestScope === scopeRef.current) setPaymentShortcutLoading(null);
    }
  };

  const handleStatusChange = async (orderId: number, newStatus: string) => {
    if (!canProcessOrders() || statusActionId !== null) return;
    const requestScope = scopeRef.current;
    setStatusActionId(orderId);
    try {
      const response = await fetch(`${API_URL}/api/ecommerce/orders/${orderId}/status`, {
        method: "PUT", headers: ecommerceRequestHeaders(), body: JSON.stringify({ status: newStatus }),
      });
      const data = await response.json().catch(() => ({}));
      if (!mountedRef.current || requestScope !== scopeRef.current) return;
      if (!response.ok) throw new Error(data.error || "Modification du statut refusée.");
      message.success("Statut de la commande actualisé.");
      await Promise.all([fetchOrders(), fetchStats()]);
      setRefreshRevision((revision) => revision + 1);
    } catch (error: any) {
      if (mountedRef.current && requestScope === scopeRef.current) message.error(error.message || "Connexion impossible.");
    } finally {
      if (mountedRef.current) setStatusActionId(null);
    }
  };

  const openPaymentModal = async (order: OrderDetails) => {
    if (!canProcessOrders()) {
      message.error(
        getPermissionErrorMessage("enregistrer un paiement", "commande")
      );
      return;
    }

    const requestScope = scopeRef.current;
    const requestId = ++paymentRequestRef.current;
    const current = () => mountedRef.current && requestScope === scopeRef.current && requestId === paymentRequestRef.current;
    setPaymentOrder(order);
    setManualDiscountEnabled(false);
    setPaymentOptions(null);
    setPaymentModalVisible(true);
    setPaymentOptionsLoading(true);
    paymentForm.resetFields();
    try {
      const response = await fetch(
        `${API_URL}/api/ecommerce/orders/${order.id}/payment-options`,
        {
          headers: ecommerceRequestHeaders(),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!current()) return;
      if (!response.ok) {
        message.error(
          data.error || "Impossible de charger les options de paiement."
        );
        setPaymentModalVisible(false);
        return;
      }

      const loadedOptions = data as OrderPaymentOptions;
      setPaymentOptions(loadedOptions);
      const suggested = loadedOptions.suggested_route;
      const defaultAccount =
        loadedOptions.options.treasury_accounts.find(
          (account) => Boolean(account.is_default)
        ) ||
        (loadedOptions.options.treasury_accounts.length === 1
          ? loadedOptions.options.treasury_accounts[0]
          : undefined);
      paymentForm.setFieldsValue({
        manual_discount_type: 'percent',
        payment_date: dayjs(),
        payment_mode_id:
          suggested?.payment_mode_id ||
          (loadedOptions.options.payment_modes.length === 1
            ? loadedOptions.options.payment_modes[0].id
            : undefined),
        treasury_account_id:
          suggested?.treasury_account_id || defaultAccount?.id,
      });
    } catch (error) {
      if (current()) {
        message.error("Erreur réseau lors du chargement des options de paiement.");
        setPaymentModalVisible(false);
      }
    } finally {
      if (current()) setPaymentOptionsLoading(false);
    }
  };

  const submitPaymentCollection = async () => {
    if (!paymentOrder || !canProcessOrders() || paymentActionLoading) return;
    const requestScope = scopeRef.current;
    let values: any;
    try {
      values = await paymentForm.validateFields();
    } catch (_) {
      return;
    }
    if (manualDiscountEnabled && (!Number(values.manual_discount_value) || paymentPreviewTotal <= 0)) {
      message.error('La remise doit être positive et laisser un total à payer positif.');
      return;
    }

    if (!mountedRef.current || requestScope !== scopeRef.current) return;
    setPaymentActionLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ecommerce/orders/${paymentOrder.id}/collect-payment`,
        {
          method: "POST",
          headers: {
            ...ecommerceRequestHeaders(),
            "X-Idempotency-Key": `ecommerce-ui-${paymentOrder.id}-${
              paymentOrder.payment_status
            }`,
          },
          body: JSON.stringify({
            amount: paymentPreviewTotal,
            ...(manualDiscountEnabled ? { manual_discount: {
              type: values.manual_discount_type,
              value: values.manual_discount_value,
            } } : {}),
            payment_mode_id: values.payment_mode_id,
            treasury_account_id: values.treasury_account_id,
            payment_date: values.payment_date?.toISOString(),
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!mountedRef.current || requestScope !== scopeRef.current) return;
      if (!response.ok) {
        message.error(
          data.error || "Erreur lors de l'enregistrement du paiement."
        );
        return;
      }

      message.success(data.message || "Paiement enregistré.");
      setPaymentModalVisible(false);
      setPaymentOrder(null);
      setPaymentOptions(null);
      paymentForm.resetFields();
      await Promise.all([fetchOrders(), fetchStats()]);
      setRefreshRevision((revision) => revision + 1);
      if (selectedOrder && requestScope === scopeRef.current) await handleViewOrder(selectedOrder);
    } catch (error) {
      message.error("Erreur réseau lors de l'enregistrement du paiement.");
    } finally {
      setPaymentActionLoading(false);
    }
  };

  const handleRefundPayment = (order: OrderDetails) => {
    if (!canProcessOrders()) {
      message.error(
        getPermissionErrorMessage("enregistrer un remboursement", "commande")
      );
      return;
    }

    const isYengaPay = order.payment_method === "yengapay";
    Modal.confirm({
      title: `Rembourser la commande ${order.order_number} ?`,
      icon: <ExclamationCircleOutlined />,
      width: 560,
      content: (
        <div>
          <p>
            ELSA créera un décaissement de {formatCurrency(order.total_amount)}
            sur le compte utilisé lors de l'encaissement, puis classera la
            commande comme remboursée.
          </p>
          {isYengaPay && (
            <Alert
              type="warning"
              showIcon
              message="Remboursement YengaPay externe requis"
              description="Cette action ne renvoie pas elle-même l'argent chez YengaPay. Continuez uniquement après avoir effectué le remboursement dans la console YengaPay."
            />
          )}
        </div>
      ),
      okText: isYengaPay
        ? "J'ai remboursé dans YengaPay"
        : "Enregistrer le remboursement",
      okButtonProps: { danger: true },
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const response = await fetch(
            `${API_URL}/api/ecommerce/orders/${order.id}/refund`,
            {
              method: "POST",
              headers: ecommerceRequestHeaders(),
              body: JSON.stringify({
                external_refund_confirmed: isYengaPay,
                refund_date: new Date().toISOString(),
              }),
            }
          );
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            message.error(
              data.error || "Erreur lors de l'enregistrement du remboursement."
            );
            return Promise.reject(new Error(data.error || "Refund failed"));
          }

          message.success(data.message || "Remboursement enregistré.");
          await Promise.all([fetchOrders(), fetchStats()]);
          if (selectedOrder) await handleViewOrder(selectedOrder);
          return undefined;
        } catch (error) {
          if (!(error instanceof Error) || error.message !== "Refund failed") {
            message.error(
              "Erreur réseau lors de l'enregistrement du remboursement."
            );
          }
          return Promise.reject(error);
        }
      },
    });
  };

  const handleDeleteOrder = async (order: Order) => {
    // ⚠️ VÉRIFICATION DE PERMISSION AVANT ACTION
    if (!canDeleteOrders()) {
      message.error({
        content: getPermissionErrorMessage("supprimer", "commande"),
        duration: 6,
      });
      return;
    }

    setOrderToDelete(order);
    setDeleteModalVisible(true);
  };

  const confirmDeleteOrder = async (values: {
    reason: string;
    confirm: string;
  }) => {
    if (!orderToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ecommerce/orders/${orderToDelete.id}`,
        {
          method: "DELETE",
          headers: ecommerceRequestHeaders(),
          body: JSON.stringify({
            reason: values.reason,
            confirm: values.confirm,
          }),
        }
      );

      if (response.ok) {
        await response.json();
        message.success({
          content: `Commande ${orderToDelete.order_number} supprimée avec succès`,
          duration: 5,
        });

        // Fermer le modal et réinitialiser
        setDeleteModalVisible(false);
        setOrderToDelete(null);

        // Recharger la liste des commandes
        fetchOrders();
        fetchStats(); // Mettre à jour les statistiques
      } else {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 403) {
          message.error(getPermissionErrorMessage("supprimer", "commande"));
        } else if (response.status === 400) {
          message.error(errorData.error || "Données de suppression invalides");
        } else {
          message.error(
            `Erreur lors de la suppression: ${
              errorData.error || response.status
            }`
          );
        }
      }
    } catch (error) {
      message.error("Erreur de connexion lors de la suppression");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditOrder = async (order: Order) => {
    // ⚠️ VÉRIFICATION DE PERMISSION AVANT ACTION
    if (!canEditOrders()) {
      message.error({
        content: getPermissionErrorMessage("modifier", "commande"),
        duration: 6,
      });
      return;
    }

    if (isFinalizedOrderStatus(order.status)) {
      message.warning(
        "Cette commande est finalisée et ne peut plus être modifiée."
      );
      return;
    }

    const requestScope = scopeRef.current;
    const requestId = ++editRequestRef.current;
    const current = () => mountedRef.current && requestScope === scopeRef.current && requestId === editRequestRef.current;
    setOrderToEdit(order);
    setEditModalVisible(true);
    setEditLoading(true);

    try {
      // Récupérer les détails complets de la commande
      const response = await fetch(
        `${API_URL}/api/ecommerce/orders/${order.id}`,
        {
          headers: ecommerceRequestHeaders(),
        }
      );

      if (!current()) return;
      if (response.ok) {
        const data = await response.json();
        if (!current()) return;
        const orderData = {
          ...data.order,
          items: data.items || [],
          fulfillments: data.fulfillments || [],
          shipping_address: data.order.shipping_address
            ? typeof data.order.shipping_address === "string"
              ? JSON.parse(data.order.shipping_address)
              : data.order.shipping_address
            : {},
          billing_address: data.order.billing_address
            ? typeof data.order.billing_address === "string"
              ? JSON.parse(data.order.billing_address)
              : data.order.billing_address
            : {},
        };

        setEditOrderDetails(orderData);

        // Préparer les données pour le formulaire
        const formData = {
          customer_email: orderData.customer_email,
          customer_phone: orderData.customer_phone || "",
          status: orderData.status,
          shipping_address: {
            address: orderData.shipping_address?.address || "",
            city: orderData.shipping_address?.city || "",
            postal_code: orderData.shipping_address?.postal_code || "",
            country: orderData.shipping_address?.country || "Burkina Faso",
          },
          billing_address: {
            address: orderData.billing_address?.address || "",
            city: orderData.billing_address?.city || "",
            postal_code: orderData.billing_address?.postal_code || "",
            country: orderData.billing_address?.country || "Burkina Faso",
          },
          shipping_amount: orderData.shipping_amount || 0,
          tax_amount: orderData.tax_amount || 0,
          notes: orderData.notes || "",
          items: orderData.items.map((item: any, index: number) => ({
            ...item,
            key: `item-${index}`,
            isNew: false,
          })),
        };

        editForm.setFieldsValue(formData);

        await fetchAvailableProducts(order.id, orderData.fulfillment_mode === 'allocated');
      } else {
        message.error("Erreur lors du chargement des détails de la commande");
        setEditModalVisible(false);
      }
    } catch (error) {
      if (current()) {
        message.error("Erreur de connexion");
        setEditModalVisible(false);
      }
    } finally {
      if (current()) setEditLoading(false);
    }
  };

  const fetchAvailableProducts = async (orderId?: number, allocated = false) => {
    if (!selectedWarehouse) return;
    const requestScope = scopeRef.current;
    const requestId = editRequestRef.current;
    setAvailableProducts([]);

    try {
      const response = await fetch(
        allocated && orderId ? `${API_URL}/api/ecommerce/orders/${orderId}/products` : `${API_URL}/api/products?warehouse_id=${selectedWarehouse}&online_visibility=true`,
        {
          headers: ecommerceRequestHeaders(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (mountedRef.current && requestScope === scopeRef.current && requestId === editRequestRef.current) setAvailableProducts(data.products || []);
      }
    } catch (error) {
    }
  };

  const confirmEditOrder = async (values: any) => {
    if (!orderToEdit) return;

    setEditLoading(true);
    try {
      // Validation et nettoyage des articles avant envoi
      const cleanedItems = values.items.map((item: any) => {
        if (!item.product_id) {
          throw new Error(
            `Article "${item.product_name || "Sans nom"}" sans ID de produit`
          );
        }

        return {
          product_id: Number(item.product_id), // S'assurer que c'est un nombre
          quantity: Number(item.quantity) || 1,
          unit_price: Number(item.unit_price) || 0,
        };
      });


      const response = await fetch(
        `${API_URL}/api/ecommerce/orders/${orderToEdit.id}`,
        {
          method: "PUT",
          headers: ecommerceRequestHeaders(),
          body: JSON.stringify({
            customer_email: values.customer_email,
            customer_phone: values.customer_phone,
            status: values.status,
            shipping_address: values.shipping_address,
            billing_address: values.billing_address,
            items: cleanedItems,
            shipping_amount: Number(values.shipping_amount) || 0,
            tax_amount: Number(values.tax_amount) || 0,
            notes: values.notes,
          }),
        }
      );

      if (response.ok) {
        await response.json();
        message.success({
          content: `Commande ${orderToEdit.order_number} modifiée avec succès`,
          duration: 5,
        });

        // Fermer le modal et réinitialiser
        setEditModalVisible(false);
        setOrderToEdit(null);
        setEditOrderDetails(null);
        editForm.resetFields();

        // Recharger la liste des commandes
        fetchOrders();
        fetchStats();
      } else {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 403) {
          message.error(getPermissionErrorMessage("modifier", "commande"));
        } else if (response.status === 400) {
          // Afficher l'erreur spécifique du serveur avec détails
          const errorMessage =
            errorData.error || "Données de modification invalides";

          let fullMessage = errorMessage;
          if (errorData.details) {
            const details = errorData.details;
            fullMessage += `\n\nDétails du problème:`;
            fullMessage += `\n• ID Produit: ${details.product_id}`;
            fullMessage += `\n• Établissement ID: ${details.warehouse_id}`;
            fullMessage += `\n• Produit existe globalement: ${
              details.product_exists ? "Oui" : "Non"
            }`;
            if (details.available_warehouses) {
              fullMessage += `\n• Disponible dans: ${details.available_warehouses}`;
            }
            fullMessage += `\n• Statut produit: ${details.product_status}`;
          }

          message.error({
            content: fullMessage,
            duration: 12, // Plus long pour lire tous les détails
          });
        } else {
          message.error(
            `Erreur lors de la modification: ${
              errorData.error || response.status
            }`
          );
        }
      }
    } catch (error: any) {

      if (error.message && error.message.includes("sans ID de produit")) {
        message.error({
          content: `Erreur de validation: ${error.message}`,
          duration: 8,
        });
      } else {
        message.error("Erreur de connexion lors de la modification");
      }
    } finally {
      setEditLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: "orange",
      confirmed: "blue",
      processing: "cyan",
      shipped: "purple",
      delivered: "green",
      cancelled: "red",
    };
    return colors[status] || "default";
  };

  const getPaymentStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: "orange",
      paid: "green",
      failed: "red",
      refunded: "purple",
    };
    return colors[status] || "default";
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      pending: "En attente",
      confirmed: "Confirmée",
      processing: "En préparation",
      shipped: "Expédiée",
      delivered: "Livrée",
      cancelled: "Annulée",
      refunded: "Remboursée",
    };
    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      pending: "En attente",
      paid: "Payé",
      failed: "Échec",
      refunded: "Remboursé",
    };
    return labels[status] || status;
  };

  const orderPaymentBadges = (order: Order) => <Space size={4} wrap>
    <Tag color={order.payment_status === "pending" ? "red" : getPaymentStatusColor(order.payment_status)}>
      {order.payment_status === "pending" ? "Non payé" : getPaymentStatusLabel(order.payment_status)}
    </Tag>
    {order.payment_status === "paid" && order.payment_reconciliation_status !== "recorded" && <Tag color="gold">À rapprocher</Tag>}
  </Space>;

  const orderReference = (order: Order) => <Tooltip title={order.order_number}>
    <Text className="ecommerce-workspace__reference" copyable={{ text: order.order_number, tooltips: ["Copier la référence complète", "Référence copiée"] }}>
      {shortenOrderReference(order.order_number)}
    </Text>
  </Tooltip>;

  const orderActions = (order: Order) => {
    const canCollect = canProcessOrders() && order.payment_status === "pending" && !["cancelled", "refunded"].includes(order.status);
    const canReconcile = canProcessOrders() && order.payment_status === "paid" && order.payment_reconciliation_status !== "recorded";
    const paymentAction = canCollect || canReconcile;
    const items: MenuProps["items"] = [];
    if (paymentAction) items.push({ key: "details", label: "Voir le détail", icon: <EyeOutlined />, onClick: () => void handleViewOrder(order) });
    if (canEditOrders()) items.push({ key: "edit", label: "Modifier la commande", icon: <EditOutlined />,
      disabled: isFinalizedOrderStatus(order.status), onClick: () => void handleEditOrder(order) });
    const transitions = getAllowedStatusOptions(order.status).filter((option) => option.value !== order.status);
    if (canProcessOrders() && transitions.length) items.push({ key: "status", label: "Changer le statut",
      children: transitions.map((option) => ({ key: option.value, label: option.label,
        onClick: () => void handleStatusChange(order.id, option.value) })) });
    if (canDeleteOrders()) items.push({ key: "delete", danger: true, icon: <DeleteOutlined />,
      label: order.payment_status === "paid" ? "Paiement confirmé : remboursement requis" : "Supprimer la commande",
      disabled: order.payment_status === "paid", onClick: () => void handleDeleteOrder(order) });
    const disabled = loading || paymentShortcutLoading !== null || statusActionId !== null;
    return <Space size={6}>
      <Button className="ecommerce-workspace__row-action" icon={canReconcile ? <LinkOutlined /> : canCollect ? <WalletOutlined /> : <EyeOutlined />}
        loading={paymentShortcutLoading === order.id} disabled={disabled && paymentShortcutLoading !== order.id}
        onClick={() => paymentAction ? void openPaymentFromList(order) : void handleViewOrder(order)}>
        {canReconcile ? "Rapprocher" : canCollect ? "Encaisser" : "Voir"}
      </Button>
      {!!items.length && <Dropdown trigger={["click"]} menu={{ items }}>
        <Button icon={<MoreOutlined />} aria-label={`Actions de la commande ${order.order_number}`} disabled={disabled} />
      </Dropdown>}
    </Space>;
  };

  const columns = [
    { title: "Commande", key: "order_number", width: 145, render: orderReference },
    { title: "Client", key: "customer", width: 175, render: (order: Order) => (
      <Tooltip title={<>{order.customer_name || "Client anonyme"}{order.customer_email && <><br />{order.customer_email}</>}</>}>
        <span className="ecommerce-workspace__name" tabIndex={0}>{order.customer_name || "Client anonyme"}</span>
      </Tooltip>
    ) },
    { title: "Boutique", key: "store", width: 140, render: (order: Order) => (
      <Tooltip title={`${order.store_name} · @${order.store_slug}`}>
        <span className="ecommerce-workspace__name">{order.store_name}</span>
      </Tooltip>
    ) },
    { title: "Articles", dataIndex: "items_count", key: "items", width: 65, align: "center" as const },
    { title: "Montant", key: "amount", width: 145, align: "right" as const,
      render: (order: Order) => <span className="ecommerce-workspace__money">{formatCurrency(order.total_amount)}</span> },
    { title: "Statut", key: "status", width: 130, render: (order: Order) =>
      <Tag color={order.status === "cancelled" ? "default" : order.status === "refunded" ? "purple" : getStatusColor(order.status)}>{getStatusLabel(order.status)}</Tag> },
    { title: "Paiement", key: "payment", width: 155, render: orderPaymentBadges },
    { title: "Date", key: "date", width: 185, fixed: "right" as const,
      render: (order: Order) => <time className="ecommerce-workspace__date" dateTime={order.created_at}>{formatOrderTimestamp(order.created_at)}</time> },
    { title: "Actions", key: "actions", width: 172, fixed: "right" as const, render: orderActions },
  ];

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };


  const orderCards = orders.map((order) => <Card key={order.id} className="ecommerce-workspace__card" size="small">
    <div className="ecommerce-workspace__card-head">{orderReference(order)}
      <Tag color={order.status === "refunded" ? "purple" : getStatusColor(order.status)}>{getStatusLabel(order.status)}</Tag></div>
    <div className="ecommerce-workspace__card-foot">
      <div><strong className="ecommerce-workspace__name">{order.customer_name || "Client anonyme"}</strong>
        <span className="ecommerce-workspace__metadata">{order.store_name} · {order.items_count} article(s)</span></div>
      <strong className="ecommerce-workspace__money">{formatCurrency(order.total_amount)}</strong>
    </div>
    <div className="ecommerce-workspace__card-foot">{orderPaymentBadges(order)}
      <time className="ecommerce-workspace__date" dateTime={order.created_at}>{formatOrderTimestamp(order.created_at)}</time></div>
    <div className="ecommerce-workspace__card-foot">{orderActions(order)}</div>
  </Card>);

  if (!hasGeneralAccess || !hasOrderAccess) {
    return <PermissionDenied title={!hasGeneralAccess ? "Accès au module E-commerce refusé" : "Visualisation des commandes refusée"}
      description="La permission de consultation des commandes e-commerce est requise." />;
  }
  if (!selectedWarehouse) {
    return <div className="ecommerce-workspace" style={workspaceStyle}>
      <Empty description="Sélectionnez un établissement pour consulter ses commandes e-commerce." />
    </div>;
  }

  const refreshWorkspace = () => {
    void fetchOrders();
    void fetchStats();
    void fetchStores();
    setRefreshRevision((revision) => revision + 1);
  };
  const filtersActive = Boolean(filters.search || filters.status || filters.payment_status ||
    filters.payment_reconciliation_status || filters.date_range || filters.store_id);
  const resetFilters = () => {
    setFilters({ search: "", status: "", payment_status: "", payment_reconciliation_status: "", date_range: null, store_id: "" });
    setPagination((previous) => ({ ...previous, current: 1 }));
  };
  const metricValue = (value: number | undefined, currency = false) =>
    statsLoading ? "…" : statsError || value === undefined ? "—" : currency ? formatCurrency(value) : value;
  const queueLabel = queueSummary.loading ? "Chargement des préparations…" : queueSummary.error
    ? "Chargement impossible · ouvrir pour réessayer"
    : queueSummary.total ? `${queueSummary.total} préparation(s) commune(s)${queueSummary.status ? ` · ${queueSummary.status}` : ""}` : "Aucune préparation commune";

  return (
    <div className="ecommerce-workspace ecommerce-workspace--orders" style={workspaceStyle} role="region" aria-label="Commandes en ligne">
      <div className="ecommerce-workspace__breadcrumb">E-commerce / Commandes en ligne</div>
      <header className="ecommerce-workspace__header">
        <div><Title level={1}>Commandes en ligne</Title>
          <div className="ecommerce-workspace__subtitle">Suivez vos commandes, leur préparation et leurs paiements.</div></div>
        <div className="ecommerce-workspace__header-actions">
          <div className="ecommerce-workspace__store"><label htmlFor="orders-store">Boutique</label>
            <Select id="orders-store" value={filters.store_id} loading={storesLoading} disabled={storesLoading || Boolean(storesError)}
              onChange={(value) => handleFilterChange("store_id", value)}
              options={[{ value: "", label: "Toutes les boutiques" }, ...stores.map((store) => ({ value: String(store.id), label: store.store_name }))]} /></div>
          <Button type="primary" icon={<ReloadOutlined />} loading={loading || statsLoading || storesLoading} onClick={refreshWorkspace}>Actualiser</Button>
        </div>
      </header>
      <section className="ecommerce-workspace__summary" aria-label="Situation des commandes de l'établissement" aria-busy={statsLoading}>
        <div className="ecommerce-workspace__metric"><span>Total commandes</span><strong>{metricValue(stats.total_orders)}</strong></div>
        <div className="ecommerce-workspace__metric"><span>En attente</span><strong>{metricValue(stats.pending_orders)}</strong></div>
        <div className="ecommerce-workspace__metric ecommerce-workspace__metric--success"><span>Complétées</span><strong>{metricValue(stats.completed_orders)}</strong></div>
        <div className="ecommerce-workspace__metric"><span>CA articles</span><strong>{metricValue(stats.merchandise_revenue, true)}</strong></div>
        <div className="ecommerce-workspace__metric"><span>Livraison facturée</span><strong>{metricValue(stats.shipping_revenue, true)}</strong></div>
        <div className="ecommerce-workspace__metric"><span>Total facturé</span><strong>{metricValue(stats.total_invoiced, true)}</strong></div>
      </section>
      <div className="ecommerce-workspace__scope">Établissement sélectionné · {filters.store_id ? stores.find((store) => Number(store.id) === Number(filters.store_id))?.store_name || "Boutique sélectionnée" : "Toutes les boutiques"} · Toutes périodes. Les filtres ci-dessous s'appliquent à la liste.</div>
      {statsError && <Alert type="warning" showIcon className="ecommerce-workspace__error" message={statsError}
        action={<Button onClick={() => void fetchStats()}>Réessayer</Button>} />}
      {storesError && <Alert type="error" showIcon className="ecommerce-workspace__error" message={storesError}
        action={<Button onClick={() => void fetchStores()}>Réessayer</Button>} />}
      <Collapse className="ecommerce-workspace__preparations" expandIconPosition="end"
        items={[{ key: "queue", forceRender: true,
          label: <div className="ecommerce-workspace__preparation-label"><strong><ShoppingOutlined /> Préparations du magasin</strong><span>{queueLabel}</span></div>,
          children: <FulfillmentQueue key={scopeKey} warehouseId={Number(selectedWarehouse)} refreshKey={refreshRevision}
            onSummaryChange={updateQueueSummary} onUpdated={() => { void fetchOrders(); void fetchStats(); }} />,
        }]} />
      <div className="ecommerce-workspace__filters">
        <EcommerceFilterField id="orders-search" label="Recherche">
          <Input id="orders-search" prefix={<SearchOutlined />} placeholder="Numéro de commande ou client…" allowClear value={filters.search}
            onChange={(event) => handleSearch(event.target.value)} />
        </EcommerceFilterField>
        <EcommerceFilterField id="orders-status" label="Statut">
          <Select id="orders-status" value={filters.status} onChange={(value) => handleFilterChange("status", value)}
            options={[{ value: "", label: "Tous les statuts" }, ...ORDER_STATUS_OPTIONS, { value: "refunded", label: "Remboursée" }]} />
        </EcommerceFilterField>
        <EcommerceFilterField id="orders-payment" label="Paiement">
          <Select id="orders-payment" value={filters.payment_status} onChange={(value) => handleFilterChange("payment_status", value)}
            options={[{ value: "", label: "Tous les paiements" }, { value: "pending", label: "Non payé" }, { value: "paid", label: "Payé" },
              { value: "failed", label: "Échec" }, { value: "refunded", label: "Remboursé" }]} />
        </EcommerceFilterField>
        <EcommerceFilterField id="orders-reconciliation" label="Rapprochement">
          <Select id="orders-reconciliation" value={filters.payment_reconciliation_status} onChange={(value) => handleFilterChange("payment_reconciliation_status", value)}
            options={[{ value: "", label: "Tous" }, { value: "routing_required", label: "À rapprocher" }, { value: "recorded", label: "Rapprochés" }]} />
        </EcommerceFilterField>
        <EcommerceFilterField id="orders-period" label="Période">
          <RangePicker id="orders-period" value={filters.date_range} format="DD/MM/YYYY" placeholder={["Début", "Fin"]}
            onChange={(value) => handleFilterChange("date_range", value)} />
        </EcommerceFilterField>
        <Button type="text" icon={<ReloadOutlined />} disabled={!filtersActive} onClick={resetFilters}>Réinitialiser</Button>
      </div>
      {ordersError && <Alert className="ecommerce-workspace__error" type="error" showIcon message={ordersError}
        action={<Button onClick={() => void fetchOrders()}>Réessayer</Button>} />}
      <div className="ecommerce-workspace__results">
        <Title level={2}>Liste des commandes</Title>
        <span>{loading ? "Chargement des commandes…" : ordersError ? "Commandes indisponibles" : `${pagination.total} résultats · ${orders.length} sur cette page`}</span>
      </div>
      {isMobile ? <div className="ecommerce-workspace__cards" role="region" aria-label="Liste des commandes">
        {loading ? <Spin /> : orderCards.length ? orderCards : <Empty description="Aucune commande ne correspond à vos filtres." />}
      </div> : <div className="ecommerce-workspace__table" ref={tableRef} role="region" aria-label="Liste des commandes">
        <Table dataSource={orders} columns={columns} rowKey="id" loading={loading} pagination={false}
          scroll={{ x: 1320, y: tableHeight }}
          locale={{ emptyText: ordersError ? "Chargement impossible" : "Aucune commande ne correspond à vos filtres." }} />
      </div>}
      <div className="ecommerce-workspace__pagination">
        <span>{pagination.total ? `${(pagination.current - 1) * pagination.pageSize + 1}–${Math.min(pagination.current * pagination.pageSize, pagination.total)} sur ${pagination.total} résultats` : "0 résultat"}</span>
        <Pagination current={pagination.current} pageSize={pagination.pageSize} total={pagination.total} showSizeChanger
          disabled={loading} pageSizeOptions={["10", "20", "50", "100"]} onChange={handleTableChange} />
      </div>

      {/* Modal détails commande */}
      <Modal
        title={`Détails commande ${selectedOrder?.order_number}`}
        open={detailModalVisible}
        onCancel={() => {
          detailsRequestRef.current += 1;
          setDetailModalVisible(false);
          setOrderDetails(null);
          setOrderDetailsLoading(false);
        }}
        footer={null}
        width={isMobile ? "calc(100vw - 24px)" : 900}
      >
        {orderDetailsLoading && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <ReloadOutlined
              spin
              style={{ fontSize: "24px", marginBottom: "1rem" }}
            />
            <div>Chargement des détails de la commande...</div>
          </div>
        )}
        {!orderDetailsLoading && !orderDetails && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Alert
              message="Aucun détail disponible"
              description="Les détails de cette commande n'ont pas pu être chargés."
              type="warning"
              showIcon
            />
          </div>
        )}
        {!orderDetailsLoading && orderDetails && (
          <div>
            <FulfillmentSummary groups={orderDetails.fulfillments} />
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Client">
                {orderDetails.customer_name || "Client anonyme"}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {orderDetails.customer_email || "Non fourni"}
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                {orderDetails.customer_phone || "Non fourni"}
              </Descriptions.Item>
              <Descriptions.Item label="Boutique">
                {orderDetails.store_name || "Boutique inconnue"}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag color={getStatusColor(orderDetails.status)}>
                  {orderDetails.status === "pending" && "En attente"}
                  {orderDetails.status === "confirmed" && "Confirmée"}
                  {orderDetails.status === "processing" && "Traitement"}
                  {orderDetails.status === "shipped" && "Expédiée"}
                  {orderDetails.status === "delivered" && "Livrée"}
                  {orderDetails.status === "cancelled" && "Annulée"}
                  {orderDetails.status === "refunded" && "Remboursée"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Paiement">
                <Tag color={getPaymentStatusColor(orderDetails.payment_status)}>
                  {orderDetails.payment_status === "pending" && "En attente"}
                  {orderDetails.payment_status === "paid" && "Payé"}
                  {orderDetails.payment_status === "failed" && "Échec"}
                  {orderDetails.payment_status === "refunded" && "Remboursé"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Adresse de livraison" span={2}>
                {/* ✨ PHASE 2: Affichage structuré de l'adresse */}
                {orderDetails.shipping_address ? (
                  <ShippingAddressDisplay address={orderDetails.shipping_address} />
                ) : (
                  <Text type="secondary">Adresse non fournie</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Frais de livraison">
                {new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "XOF",
                }).format(orderDetails.shipping_amount || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Articles après remise">
                {formatCurrency(Number(orderDetails.total_amount) - Number(orderDetails.shipping_amount || 0) - Number(orderDetails.tax_amount || 0))}
              </Descriptions.Item>
              {Number(orderDetails.discount_amount || 0) > 0 && (
                <Descriptions.Item label={orderDetails.promotion_code ? `Code ${orderDetails.promotion_code}` : 'Remise interne'}>
                  −{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(Number(orderDetails.discount_amount || 0))}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Taxes">
                {new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "XOF",
                }).format(orderDetails.tax_amount || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                <Text strong style={{ color: "#52c41a", fontSize: "16px" }}>
                  {new Intl.NumberFormat("fr-FR", {
                    style: "currency",
                    currency: "XOF",
                  }).format(orderDetails.total_amount)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Date de commande">
                {dayjs(orderDetails.created_at).format("DD/MM/YYYY HH:mm")}
              </Descriptions.Item>
              {orderDetails.payment_method && (
                <Descriptions.Item label="Mode de paiement">
                  {orderDetails.payment_method}
                </Descriptions.Item>
              )}
              {orderDetails.payment_reference && (
                <Descriptions.Item label="Référence paiement">
                  <Text code>{orderDetails.payment_reference}</Text>
                </Descriptions.Item>
              )}
              {orderDetails.notes && (
                <Descriptions.Item label="Notes" span={2}>
                  <Text italic>{orderDetails.notes}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <DeliveryFinancePanel orderId={orderDetails.id} orderStatus={orderDetails.status} onUpdated={() => { void fetchStats(); }} />

            <div style={{ marginTop: 16 }}>
              {orderDetails.payment_reconciliation_status === "recorded" && (
                <Alert
                  type="success"
                  showIcon
                  message="Paiement rapproché"
                  description="L'encaissement apparaît dans GestionVentes et sur le compte de trésorerie sélectionné."
                />
              )}
              {orderDetails.payment_reconciliation_status ===
                "routing_required" && (
                <Alert
                  type="warning"
                  showIcon
                  message="Paiement reçu à rapprocher"
                  description={
                    orderDetails.payment_reconciliation_message ||
                    "Sélectionnez le mode réel et le compte de trésorerie afin de créer l'écriture financière manquante."
                  }
                />
              )}
              {orderDetails.payment_status === "pending" && (
                <Alert
                  type="info"
                  showIcon
                  message="Aucun encaissement confirmé"
                  description={
                    orderDetails.status === "delivered"
                      ? "La vente est réalisée, mais son règlement reste à recevoir : la commande constitue une créance client."
                      : "La commande n'est pas encore payée. La vente ne sera réalisée qu'à la livraison."
                  }
                />
              )}

              <Space wrap style={{ marginTop: 12 }}>
                {canProcessOrders() &&
                  orderDetails.payment_status === "pending" &&
                  !["cancelled", "refunded"].includes(orderDetails.status) && (
                    <Button
                      type="primary"
                      icon={<WalletOutlined />}
                      onClick={() => void openPaymentModal(orderDetails)}
                    >
                      Enregistrer l'encaissement
                    </Button>
                  )}
                {canProcessOrders() &&
                  orderDetails.payment_status === "paid" &&
                  orderDetails.payment_reconciliation_status !== "recorded" && (
                    <Button
                      type="primary"
                      icon={<WalletOutlined />}
                      onClick={() => void openPaymentModal(orderDetails)}
                    >
                      Rapprocher le paiement
                    </Button>
                  )}
                {canProcessOrders() &&
                  orderDetails.payment_status === "paid" &&
                  orderDetails.payment_reconciliation_status === "recorded" && (
                    <Button
                      danger
                      icon={<RollbackOutlined />}
                      onClick={() => handleRefundPayment(orderDetails)}
                    >
                      Enregistrer un remboursement
                    </Button>
                  )}
                {orderDetails.payment_summary && (
                  <Text type="secondary">
                    Net encaissé :{" "}
                    <Text strong>
                      {formatCurrency(
                        orderDetails.payment_summary.net_collected_amount
                      )}
                    </Text>
                  </Text>
                )}
              </Space>
            </div>

            {!!orderDetails.payments?.length && (
              <>
                <Title
                  level={5}
                  style={{ marginTop: "1.5rem", marginBottom: "0.75rem" }}
                >
                  Écritures de paiement
                </Title>
                <Table
                  dataSource={orderDetails.payments}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ x: 650 }}
                  columns={[
                    {
                      title: "Type",
                      dataIndex: "event_type",
                      key: "event_type",
                      render: (eventType: "capture" | "refund") => (
                        <Tag color={eventType === "capture" ? "green" : "red"}>
                          {eventType === "capture"
                            ? "Encaissement"
                            : "Remboursement"}
                        </Tag>
                      ),
                    },
                    {
                      title: "N° paiement",
                      dataIndex: "payment_number",
                      key: "payment_number",
                    },
                    {
                      title: "Mode",
                      dataIndex: "payment_mode_name",
                      key: "payment_mode_name",
                    },
                    {
                      title: "Compte",
                      dataIndex: "treasury_account_name",
                      key: "treasury_account_name",
                    },
                    {
                      title: "Montant",
                      dataIndex: "amount",
                      key: "amount",
                      align: "right" as const,
                      render: (amount: number) => formatCurrency(amount),
                    },
                  ]}
                />
              </>
            )}

            <Title
              level={4}
              style={{ marginTop: "1.5rem", marginBottom: "1rem" }}
            >
              Articles commandés ({orderDetails.items?.length || 0})
            </Title>
            <Table
              dataSource={orderDetails.items || []}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: 600 }}
              columns={[
                {
                  title: "Produit",
                  dataIndex: "product_name",
                  key: "product_name",
                  width: 200,
                  render: (text: string) => (
                    <div style={{ fontWeight: 500 }}>{text}</div>
                  ),
                },
                {
                  title: "Quantité",
                  dataIndex: "quantity",
                  key: "quantity",
                  align: "center",
                  width: 80,
                  render: (qty: number) => <Badge count={qty} color="blue" />,
                },
                {
                  title: "Prix unitaire",
                  dataIndex: "unit_price",
                  key: "unit_price",
                  width: 120,
                  render: (price: number) =>
                    new Intl.NumberFormat("fr-FR", {
                      style: "currency",
                      currency: "XOF",
                    }).format(price),
                },
                {
                  title: "Total ligne",
                  dataIndex: "total_price",
                  key: "total_price",
                  width: 120,
                  render: (total: number) => (
                    <Text strong style={{ color: "#52c41a" }}>
                      {new Intl.NumberFormat("fr-FR", {
                        style: "currency",
                        currency: "XOF",
                      }).format(total)}
                    </Text>
                  ),
                },
              ]}
              summary={(pageData) => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Total commande</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <Text strong style={{ color: "#52c41a", fontSize: "16px" }}>
                      {new Intl.NumberFormat("fr-FR", {
                        style: "currency",
                        currency: "XOF",
                      }).format(orderDetails.total_amount)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
        )}
      </Modal>

      <Modal
        title={
          paymentOrder?.payment_status === "paid"
            ? `Rapprocher le paiement ${paymentOrder.order_number}`
            : `Enregistrer l'encaissement ${paymentOrder?.order_number || ""}`
        }
        open={paymentModalVisible}
        onCancel={() => {
          if (paymentActionLoading) return;
          paymentRequestRef.current += 1;
          setPaymentModalVisible(false);
          setPaymentOrder(null);
          setPaymentOptions(null);
          paymentForm.resetFields();
        }}
        onOk={() => void submitPaymentCollection()}
        okText={
          paymentOrder?.payment_status === "paid"
            ? "Rapprocher le paiement"
            : "Enregistrer l'encaissement"
        }
        confirmLoading={paymentActionLoading}
        okButtonProps={{
          disabled:
            paymentOptionsLoading ||
            !paymentOptions?.options?.treasury_accounts?.length,
        }}
        cancelText="Annuler"
        width={isMobile ? "calc(100vw - 24px)" : 640}
        destroyOnClose
      >
        {paymentOrder && (
          <>
            <Alert
              type={paymentOrder.payment_status === "paid" ? "warning" : "info"}
              showIcon
              style={{ marginBottom: 16 }}
              message={
                paymentOrder.payment_status === "paid"
                  ? "Le paiement est visible mais son écriture financière manque"
                  : "Indiquez le moyen réellement utilisé au moment de l'encaissement"
              }
              description={
                paymentOrder.payment_status === "paid"
                  ? "Cette opération crée l'encaissement manquant dans GestionVentes et la trésorerie, sans reconnaître une seconde vente."
                  : "Paiement à la livraison décrit le moment du règlement. Sélectionnez ici le moyen réel (espèces, banque ou Mobile Money) et son compte destinataire."
              }
            />

            {!paymentOptionsLoading &&
              !paymentOptions?.options?.treasury_accounts?.length && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Aucun compte commercial éligible"
                  description="Créez un compte de trésorerie actif pour cet établissement avant d'enregistrer l'encaissement."
                />
              )}

            <Form form={paymentForm} layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="Montant à encaisser">
                    <InputNumber
                      style={{ width: "100%" }}
                      disabled
                      value={paymentPreviewTotal}
                      formatter={(value) =>
                        `${Number(value || 0).toLocaleString("fr-FR")} FCFA`
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Date d'encaissement"
                    name="payment_date"
                    rules={[
                      { required: true, message: "La date est obligatoire." },
                    ]}
                  >
                    <DatePicker
                      showTime
                      format="DD/MM/YYYY HH:mm"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Mode de paiement réel"
                    name="payment_mode_id"
                    rules={[
                      {
                        required: true,
                        message: "Sélectionnez le mode réellement utilisé.",
                      },
                    ]}
                  >
                    <Select
                      loading={paymentOptionsLoading}
                      showSearch
                      optionFilterProp="label"
                      placeholder="Espèces, banque, Mobile Money…"
                      options={(
                        paymentOptions?.options?.payment_modes || []
                      ).map((mode) => ({
                        value: mode.id,
                        label: `${mode.name}${
                          mode.mode_type ? ` · ${mode.mode_type}` : ""
                        }`,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Compte de trésorerie"
                    name="treasury_account_id"
                    rules={[
                      {
                        required: true,
                        message: "Sélectionnez le compte destinataire.",
                      },
                    ]}
                  >
                    <Select
                      loading={paymentOptionsLoading}
                      showSearch
                      optionFilterProp="label"
                      placeholder="Compte qui reçoit le règlement"
                      options={(
                        paymentOptions?.options?.treasury_accounts || []
                      ).map((account) => ({
                        value: account.id,
                        label: `${account.name}${
                          account.account_type
                            ? ` · ${account.account_type}`
                            : ""
                        }${
                          account.currency_code
                            ? ` · ${account.currency_code}`
                            : ""
                        }${account.is_default ? " · Par défaut" : ""}`,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {(paymentOrder.payment_method === 'cod' || (!paymentOrder.payment_method && !paymentOrder.payment_reference)) &&
                paymentOrder.payment_status === 'pending' &&
                !paymentOrder.promotion_code && canEditOrders() && (
                <>
                  <Form.Item label="Appliquer une remise interne">
                    <Switch checked={manualDiscountEnabled} onChange={setManualDiscountEnabled} />
                  </Form.Item>
                  {manualDiscountEnabled && <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="manual_discount_type" label="Type de remise" rules={[{ required: true }]}>
                        <Select options={[{ value: 'percent', label: 'Pourcentage' }, { value: 'fixed', label: 'Montant fixe' }]} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="manual_discount_value" label={manualDiscountType === 'fixed' ? 'Montant FCFA' : 'Pourcentage'} rules={[{ required: true, message: 'Indiquez la remise.' }]}>
                        <InputNumber style={{ width: '100%' }} min={0.01} max={manualDiscountType === 'fixed' ? 99999999.99 : 100} precision={2} />
                      </Form.Item>
                    </Col>
                  </Row>}
                  {manualDiscountEnabled && previewDiscount > 0 && <Alert type="info" showIcon message={`Remise : ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(previewDiscount)} · Total à encaisser : ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(paymentPreviewTotal)}`} />}
                </>
              )}
            </Form>
          </>
        )}
      </Modal>

      {/* Modal de modification de commande */}
      <Modal
        className="ecommerce-order-edit-modal"
        title={
          <div className="ecommerce-order-edit__modal-title">
            <div className="ecommerce-order-edit__title-main">
              <span className="ecommerce-order-edit__title-icon">
                <EditOutlined />
              </span>
              <div className="ecommerce-order-edit__title-copy">
                <div className="ecommerce-order-edit__heading">
                  Modifier la commande
                </div>
                <div className="ecommerce-order-edit__title-meta">
                  <span
                    className="ecommerce-order-edit__order-number"
                    title={orderToEdit?.order_number}
                  >
                    {orderToEdit?.order_number}
                  </span>
                  <Tag color={getStatusColor(editedOrderInitialStatus)}>
                    {getStatusLabel(editedOrderInitialStatus)}
                  </Tag>
                </div>
              </div>
            </div>
            <div className="ecommerce-order-edit__history-note">
              <InfoCircleOutlined />
              <span>Les modifications seront enregistrées dans l’historique.</span>
            </div>
          </div>
        }
        open={editModalVisible}
        onCancel={() => {
          if (!editLoading) {
            setEditModalVisible(false);
            setOrderToEdit(null);
            setEditOrderDetails(null);
            editForm.resetFields();
          }
        }}
        footer={null}
        width={isMobile ? "calc(100vw - 16px)" : "90vw"}
        style={{ maxWidth: 1680, paddingBottom: 0 }}
        centered
        maskClosable={!editLoading}
        closable={!editLoading}
        destroyOnClose
      >
        {orderToEdit && (
          editLoading && !editOrderDetails ? (
            <div className="ecommerce-order-edit__loading">
              <Spin size="large" tip="Chargement de la commande…" />
            </div>
          ) : (
            <Form
              form={editForm}
              className="ecommerce-order-edit__form"
              layout="vertical"
              onFinish={confirmEditOrder}
              disabled={editLoading}
            >
              <div className="ecommerce-order-edit__layout">
                <main className="ecommerce-order-edit__main">
                  <section className="ecommerce-order-edit__primary-section">
                    <div className="ecommerce-order-edit__section-heading">
                      <UserOutlined />
                      <h3>Informations principales</h3>
                    </div>

                    <div className="ecommerce-order-edit__primary-grid">
                      <Form.Item
                        label="Email client"
                        name="customer_email"
                        rules={[
                          {
                            type: "email",
                            message: "Format d'email invalide",
                          },
                        ]}
                      >
                        <Input
                          prefix={<MailOutlined />}
                          placeholder="client@email.com"
                        />
                      </Form.Item>

                      <Form.Item label="Téléphone client" name="customer_phone">
                        <Input
                          prefix={<PhoneOutlined />}
                          placeholder="+226 XX XX XX XX"
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <span className="ecommerce-order-edit__field-label">
                            Statut de la commande
                            {!canProcessOrders() && (
                              <Tooltip title="Permission 'Traitement des commandes' requise pour modifier le statut">
                                <LockOutlined aria-label="Lecture seule" />
                              </Tooltip>
                            )}
                          </span>
                        }
                        name="status"
                        rules={[
                          {
                            required: true,
                            message: "Le statut est obligatoire",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Sélectionner un statut"
                          disabled={!canProcessOrders()}
                        >
                          {getAllowedStatusOptions(
                            editOrderDetails?.status || "pending"
                          ).map((option) => (
                            <Option key={option.value} value={option.value}>
                              <Tag color={option.color}>{option.label}</Tag>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Frais de livraison (FCFA)"
                        name="shipping_amount"
                      >
                        <InputNumber
                          min={0}
                          controls={false}
                          placeholder="0"
                          formatter={(value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                          }
                          parser={(value) => value!.replace(/\s?/g, "") as any}
                        />
                      </Form.Item>

                      <Form.Item
                        label="Montant des taxes (FCFA)"
                        name="tax_amount"
                      >
                        <InputNumber
                          min={0}
                          controls={false}
                          placeholder="0"
                          formatter={(value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                          }
                          parser={(value) => value!.replace(/\s?/g, "") as any}
                        />
                      </Form.Item>
                    </div>
                  </section>

                  <section className="ecommerce-order-edit__items-section">
                    <div className="ecommerce-order-edit__section-heading ecommerce-order-edit__section-heading--between">
                      <div>
                        <ShoppingCartOutlined />
                        <h3>Articles de la commande</h3>
                      </div>
                      <Text type="secondary">
                        {editedItems.length}{" "}
                        {editedItems.length > 1 ? "articles" : "article"}
                      </Text>
                    </div>

                    {editedInvalidItemsCount > 0 && (
                      <Alert
                        className="ecommerce-order-edit__items-alert"
                        message={`${editedInvalidItemsCount} ligne(s) à compléter`}
                        description="Sélectionnez un produit pour chaque nouvelle ligne avant d’enregistrer."
                        type="warning"
                        showIcon
                      />
                    )}

                    <Form.List name="items">
                      {(fields, { add, remove }) => (
                        <>
                          <div
                            className="ecommerce-order-edit__items-viewport"
                            role="table"
                            aria-label="Articles de la commande"
                          >
                            <div
                              className="ecommerce-order-edit__item-header"
                              role="row"
                            >
                              <span role="columnheader">Article</span>
                              <span role="columnheader">Quantité</span>
                              <span role="columnheader">
                                Prix unitaire (FCFA)
                              </span>
                              <span role="columnheader">Total</span>
                              <span role="columnheader">Action</span>
                            </div>

                            {fields.map(({ key, name, ...restField }) => {
                              const currentItem = editedItems[name];
                              const quantity = toSafeNumber(
                                currentItem?.quantity
                              );
                              const unitPrice = toSafeNumber(
                                currentItem?.unit_price
                              );
                              const lineTotal = quantity * unitPrice;
                              const isNewItem = Boolean(currentItem?.isNew);

                              const updateQuantity = (change: number) => {
                                const nextQuantity = Math.max(
                                  1,
                                  quantity + change
                                );
                                editForm.setFieldValue(
                                  ["items", name, "quantity"],
                                  nextQuantity
                                );
                              };

                              return (
                                <div
                                  className="ecommerce-order-edit__item-row"
                                  role="row"
                                  key={key}
                                >
                                  <div
                                    className="ecommerce-order-edit__item-cell ecommerce-order-edit__item-cell--product"
                                    role="cell"
                                  >
                                    <span className="ecommerce-order-edit__mobile-label">
                                      Article
                                    </span>
                                    <Form.Item
                                      {...restField}
                                      name={[name, "product_name"]}
                                      hidden
                                    >
                                      <Input />
                                    </Form.Item>

                                    {isNewItem ? (
                                      <Form.Item
                                        {...restField}
                                        name={[name, "product_id"]}
                                        className="ecommerce-order-edit__item-form-item"
                                        rules={[
                                          {
                                            required: true,
                                            message: "Sélectionnez un produit.",
                                          },
                                        ]}
                                      >
                                        <Select
                                          showSearch
                                          optionFilterProp="label"
                                          placeholder="Choisir un produit"
                                          options={availableProducts
                                            .filter(
                                              (product) =>
                                                !editedItems.some(
                                                  (
                                                    other: any,
                                                    index: number
                                                  ) =>
                                                    index !== name &&
                                                    Number(other?.product_id) ===
                                                      Number(product.id)
                                                )
                                            )
                                            .map((product) => ({
                                              value: Number(product.id),
                                              label: product.item_code
                                                ? `${product.name} · ${product.item_code}`
                                                : product.name,
                                            }))}
                                          onChange={(productId) => {
                                            const product =
                                              availableProducts.find(
                                                (candidate) =>
                                                  Number(candidate.id) ===
                                                  Number(productId)
                                              );
                                            editForm.setFieldValue(
                                              ["items", name, "product_name"],
                                              product?.name || ""
                                            );
                                            editForm.setFieldValue(
                                              ["items", name, "unit_price"],
                                              Number(
                                                product?.sales_price ??
                                                  product?.online_price ??
                                                  0
                                              )
                                            );
                                          }}
                                        />
                                      </Form.Item>
                                    ) : (
                                      <>
                                        <Form.Item
                                          {...restField}
                                          name={[name, "product_id"]}
                                          hidden
                                          rules={[
                                            {
                                              required: true,
                                              message:
                                                "Identifiant produit requis.",
                                            },
                                          ]}
                                        >
                                          <Input />
                                        </Form.Item>
                                        <div className="ecommerce-order-edit__product-name">
                                          <ShoppingOutlined />
                                          <span title={currentItem?.product_name}>
                                            {currentItem?.product_name ||
                                              "Article sans nom"}
                                          </span>
                                        </div>
                                      </>
                                    )}
                                  </div>

                                  <div
                                    className="ecommerce-order-edit__item-cell ecommerce-order-edit__item-cell--quantity"
                                    role="cell"
                                  >
                                    <span className="ecommerce-order-edit__mobile-label">
                                      Quantité
                                    </span>
                                    <div className="ecommerce-order-edit__quantity-stepper">
                                      <Button
                                        icon={<MinusOutlined />}
                                        aria-label="Diminuer la quantité"
                                        onClick={() => updateQuantity(-1)}
                                        disabled={quantity <= 1}
                                      />
                                      <Form.Item
                                        {...restField}
                                        name={[name, "quantity"]}
                                        className="ecommerce-order-edit__item-form-item"
                                        rules={[
                                          {
                                            required: true,
                                            message: "Qté requise",
                                          },
                                          {
                                            type: "number",
                                            min: 1,
                                            message: "Quantité minimum : 1",
                                          },
                                        ]}
                                      >
                                        <InputNumber
                                          min={1}
                                          controls={false}
                                          aria-label="Quantité"
                                        />
                                      </Form.Item>
                                      <Button
                                        icon={<PlusOutlined />}
                                        aria-label="Augmenter la quantité"
                                        onClick={() => updateQuantity(1)}
                                      />
                                    </div>
                                  </div>

                                  <div
                                    className="ecommerce-order-edit__item-cell ecommerce-order-edit__item-cell--price"
                                    role="cell"
                                  >
                                    <span className="ecommerce-order-edit__mobile-label">
                                      Prix unitaire (FCFA)
                                    </span>
                                    <Form.Item
                                      {...restField}
                                      name={[name, "unit_price"]}
                                      className="ecommerce-order-edit__item-form-item"
                                      rules={[
                                        {
                                          required: true,
                                          message: "Prix requis",
                                        },
                                        {
                                          validator: (_, value) => {
                                            if (
                                              value === undefined ||
                                              value === null ||
                                              value === ""
                                            ) {
                                              return Promise.reject(
                                                new Error("Prix requis")
                                              );
                                            }
                                            if (value < 0) {
                                              return Promise.reject(
                                                new Error("Prix minimum: 0")
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                      ]}
                                    >
                                      <InputNumber
                                        min={0}
                                        controls={false}
                                        aria-label="Prix unitaire"
                                        formatter={(value) =>
                                          `${value}`.replace(
                                            /\B(?=(\d{3})+(?!\d))/g,
                                            " "
                                          )
                                        }
                                        parser={(value) =>
                                          value!.replace(/\s?/g, "") as any
                                        }
                                      />
                                    </Form.Item>
                                  </div>

                                  <div
                                    className="ecommerce-order-edit__item-cell ecommerce-order-edit__item-cell--total"
                                    role="cell"
                                  >
                                    <span className="ecommerce-order-edit__mobile-label">
                                      Total
                                    </span>
                                    <strong>{formatFcfa(lineTotal)}</strong>
                                  </div>

                                  <div
                                    className="ecommerce-order-edit__item-cell ecommerce-order-edit__item-cell--action"
                                    role="cell"
                                  >
                                    <span className="ecommerce-order-edit__mobile-label">
                                      Action
                                    </span>
                                    <Tooltip
                                      title={
                                        fields.length <= 1
                                          ? "La commande doit conserver au moins un article."
                                          : "Supprimer cet article"
                                      }
                                    >
                                      <span>
                                        <Button
                                          type="text"
                                          danger
                                          icon={<DeleteOutlined />}
                                          onClick={() => remove(name)}
                                          disabled={fields.length <= 1}
                                          aria-label="Supprimer cet article"
                                        />
                                      </span>
                                    </Tooltip>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <Button
                            className="ecommerce-order-edit__add-item"
                            type="dashed"
                            onClick={() =>
                              add({
                                product_id: null,
                                product_name: "",
                                quantity: 1,
                                unit_price: 0,
                                isNew: true,
                              })
                            }
                            block
                            icon={<PlusOutlined />}
                          >
                            Ajouter un article
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </section>

                  <Collapse
                    className="ecommerce-order-edit__additional"
                    expandIconPosition="end"
                    items={[
                      {
                        key: "additional-information",
                        forceRender: true,
                        label: (
                          <div className="ecommerce-order-edit__additional-label">
                            <span className="ecommerce-order-edit__additional-title">
                              <FileTextOutlined />
                              Informations complémentaires
                            </span>
                            <span className="ecommerce-order-edit__additional-hint">
                              Adresses de livraison et de facturation, notes
                            </span>
                          </div>
                        ),
                        children: (
                          <div className="ecommerce-order-edit__additional-content">
                            <div className="ecommerce-order-edit__address-block">
                              <div className="ecommerce-order-edit__subsection-title">
                                <EnvironmentOutlined />
                                Adresse de livraison
                              </div>
                              <Form.Item
                                label="Adresse"
                                name={["shipping_address", "address"]}
                              >
                                <Input.TextArea
                                  rows={2}
                                  placeholder="Rue, avenue, quartier…"
                                />
                              </Form.Item>
                              <div className="ecommerce-order-edit__address-grid">
                                <Form.Item
                                  label="Ville"
                                  name={["shipping_address", "city"]}
                                >
                                  <Input placeholder="Ouagadougou" />
                                </Form.Item>
                                <Form.Item
                                  label="Code postal"
                                  name={["shipping_address", "postal_code"]}
                                >
                                  <Input placeholder="01 BP 1234" />
                                </Form.Item>
                              </div>
                              <Form.Item
                                label="Pays"
                                name={["shipping_address", "country"]}
                              >
                                <Input placeholder="Burkina Faso" />
                              </Form.Item>
                            </div>

                            <div className="ecommerce-order-edit__address-block">
                              <div className="ecommerce-order-edit__subsection-title">
                                <EnvironmentOutlined />
                                Adresse de facturation
                              </div>
                              <Form.Item
                                label="Adresse"
                                name={["billing_address", "address"]}
                              >
                                <Input.TextArea
                                  rows={2}
                                  placeholder="Rue, avenue, quartier…"
                                />
                              </Form.Item>
                              <div className="ecommerce-order-edit__address-grid">
                                <Form.Item
                                  label="Ville"
                                  name={["billing_address", "city"]}
                                >
                                  <Input placeholder="Ouagadougou" />
                                </Form.Item>
                                <Form.Item
                                  label="Code postal"
                                  name={["billing_address", "postal_code"]}
                                >
                                  <Input placeholder="01 BP 1234" />
                                </Form.Item>
                              </div>
                              <Form.Item
                                label="Pays"
                                name={["billing_address", "country"]}
                              >
                                <Input placeholder="Burkina Faso" />
                              </Form.Item>
                            </div>

                            <Form.Item
                              className="ecommerce-order-edit__notes"
                              label="Notes et commentaires"
                              name="notes"
                            >
                              <Input.TextArea
                                rows={3}
                                placeholder="Notes sur la commande, instructions spéciales…"
                                maxLength={1000}
                                showCount
                              />
                            </Form.Item>
                          </div>
                        ),
                      },
                    ]}
                  />
                </main>

                <aside
                  className="ecommerce-order-edit__summary"
                  aria-label="Résumé de la commande"
                >
                  <div className="ecommerce-order-edit__summary-content">
                    <div className="ecommerce-order-edit__summary-heading">
                      <ShoppingCartOutlined />
                      <h3>Résumé de la commande</h3>
                    </div>
                    <Text type="secondary">
                      {editedItems.length}{" "}
                      {editedItems.length > 1 ? "articles" : "article"}
                    </Text>

                    <div
                      className="ecommerce-order-edit__summary-metrics"
                      aria-live="polite"
                    >
                      <div>
                        <span>Sous-total articles</span>
                        <strong>{formatFcfa(editedSubtotal)}</strong>
                      </div>
                      <div>
                        <span>Frais de livraison</span>
                        <strong>
                          {formatFcfa(toSafeNumber(editedShippingAmount))}
                        </strong>
                      </div>
                      <div>
                        <span>Taxes</span>
                        <strong>{formatFcfa(toSafeNumber(editedTaxAmount))}</strong>
                      </div>
                      {editedDiscountAmount > 0 && (
                        <div className="ecommerce-order-edit__summary-discount">
                          <span>
                            {editOrderDetails?.promotion_code
                              ? `Code ${editOrderDetails.promotion_code}`
                              : "Remise interne"}
                          </span>
                          <strong>−{formatFcfa(editedDiscountAmount)}</strong>
                        </div>
                      )}
                    </div>

                    <div className="ecommerce-order-edit__summary-total">
                      <span>Total final</span>
                      <strong>{formatFcfa(editedFinalTotal)}</strong>
                    </div>

                    <Alert
                      className="ecommerce-order-edit__permission-state"
                      type={editedOrderIsFinalized ? "error" : "success"}
                      showIcon
                      message={
                        editedOrderIsFinalized
                          ? "Commande finalisée · modification bloquée"
                          : "Commande non livrée · modification autorisée"
                      }
                      description={
                        editedOrderIsFinalized
                          ? "Cette commande ne peut plus être modifiée."
                          : "Vous pouvez modifier cette commande. Les changements seront enregistrés dans l’historique."
                      }
                    />
                  </div>

                  <div className="ecommerce-order-edit__summary-actions">
                    <Button
                      onClick={() => {
                        setEditModalVisible(false);
                        setOrderToEdit(null);
                        setEditOrderDetails(null);
                        editForm.resetFields();
                      }}
                      disabled={editLoading}
                      size="large"
                    >
                      Annuler
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={editLoading}
                      disabled={
                        editedOrderIsFinalized ||
                        editedItems.length === 0 ||
                        editedInvalidItemsCount > 0
                      }
                      icon={<SaveOutlined />}
                      size="large"
                    >
                      {editLoading
                        ? "Sauvegarde en cours…"
                        : "Sauvegarder les modifications"}
                    </Button>
                  </div>
                </aside>
              </div>
            </Form>
          )
        )}
      </Modal>

      {/* Modal de suppression de commande */}
      <Modal
        title={
          <div
            style={{ display: "flex", alignItems: "center", color: "#ff4d4f" }}
          >
            <ExclamationCircleOutlined
              style={{ marginRight: "8px", fontSize: "20px" }}
            />
            Supprimer la commande {orderToDelete?.order_number}
          </div>
        }
        open={deleteModalVisible}
        onCancel={() => {
          if (!deleteLoading) {
            setDeleteModalVisible(false);
            setOrderToDelete(null);
          }
        }}
        footer={null}
        width={isMobile ? "calc(100vw - 24px)" : 600}
        maskClosable={!deleteLoading}
        closable={!deleteLoading}
      >
        {orderToDelete && (
          <div>
            <Alert
              message="Attention : Action irréversible"
              description={
                <div>
                  <p>
                    Vous êtes sur le point de supprimer définitivement cette
                    commande :
                  </p>
                  <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                    <li>
                      <strong>N° Commande :</strong>{" "}
                      {orderToDelete.order_number}
                    </li>
                    <li>
                      <strong>Client :</strong> {orderToDelete.customer_name}
                    </li>
                    <li>
                      <strong>Montant :</strong>{" "}
                      {new Intl.NumberFormat("fr-FR", {
                        style: "currency",
                        currency: "XOF",
                      }).format(orderToDelete.total_amount)}
                    </li>
                    <li>
                      <strong>Statut :</strong> {orderToDelete.status}
                    </li>
                  </ul>
                  <p style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                    Cette action ne peut pas être annulée. Toutes les données de
                    cette commande seront perdues.
                  </p>
                </div>
              }
              type="error"
              showIcon
              style={{ marginBottom: "20px" }}
            />

            <Form
              onFinish={confirmDeleteOrder}
              layout="vertical"
              disabled={deleteLoading}
            >
              <Form.Item
                label="Raison de la suppression"
                name="reason"
                rules={[
                  {
                    required: true,
                    message: "Veuillez indiquer la raison de la suppression",
                  },
                  {
                    min: 10,
                    message: "La raison doit contenir au moins 10 caractères",
                  },
                ]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Ex: Commande frauduleuse, demande client, erreur système..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label={
                  <div>
                    Pour confirmer, tapez <Text code>DELETE</Text> dans le champ
                    ci-dessous :
                  </div>
                }
                name="confirm"
                rules={[
                  {
                    required: true,
                    message: 'Veuillez taper "DELETE" pour confirmer',
                  },
                  {
                    validator: (_, value) => {
                      if (value === "DELETE") {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          'Vous devez taper exactement "DELETE" pour confirmer'
                        )
                      );
                    },
                  },
                ]}
              >
                <Input
                  placeholder="Tapez DELETE pour confirmer"
                  autoComplete="off"
                />
              </Form.Item>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <Space>
                  <Button
                    onClick={() => {
                      setDeleteModalVisible(false);
                      setOrderToDelete(null);
                    }}
                    disabled={deleteLoading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="primary"
                    danger
                    htmlType="submit"
                    loading={deleteLoading}
                    icon={<DeleteOutlined />}
                  >
                    {deleteLoading
                      ? "Suppression..."
                      : "Supprimer définitivement"}
                  </Button>
                </Space>
              </div>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CommandesEnLigne;
